import React, { useMemo } from 'react';

import { Stack, Text, Wrap, WrapItem } from '@chakra-ui/react';

import { Tag } from '@/components/core';
import { FONTS_OPACITY } from '@/constant';
import { JobStepsBreakdown, JobTechnicalDetails } from '@/types/search';

interface OutcomeExpandedDetailsProps {
  technicalDetails: JobTechnicalDetails | null;
  stepsBreakdown: JobStepsBreakdown | null;
  summary?: string | null;
  fullText?: string | null;
}

interface DetailBlockProps {
  label: string;
  value: string;
}

const DetailBlock: React.FC<DetailBlockProps> = ({ label, value }) => (
  <Stack gap={0.5} align='stretch'>
    <Text
      fontSize='xs'
      lineHeight='4'
      fontWeight='semibold'
      color='tabs.font-selected'
      textTransform='uppercase'
      letterSpacing='0.02em'
    >
      {label}
    </Text>
    <Text
      fontSize='xs'
      lineHeight='4'
      opacity={FONTS_OPACITY}
      color='fonts.primary'
    >
      {value}
    </Text>
  </Stack>
);

interface ChipGroupProps {
  label: string;
  items: string[];
  emphasize?: boolean;
}

const CHIP_TAG_SX = {
  whiteSpace: 'normal',
  height: 'auto',
  minH: 6,
  py: 1,
  alignItems: 'flex-start',
  '& .chakra-tag__label': {
    whiteSpace: 'normal',
    overflow: 'visible',
    textOverflow: 'clip',
    lineHeight: '1.25',
  },
} as const;

const ChipGroup: React.FC<ChipGroupProps> = ({
  label,
  items,
  emphasize = false,
}) => {
  if (items.length === 0) return null;

  return (
    <Stack gap={1.5} align='stretch' minW={0}>
      <Text
        fontSize='xs'
        lineHeight='4'
        fontWeight='semibold'
        color={emphasize ? 'tags.gold.font' : 'tabs.font-selected'}
        textTransform='uppercase'
        letterSpacing='0.02em'
      >
        {label}
      </Text>
      <Wrap spacing={1.5} shouldWrapChildren>
        {items.map(item => (
          <WrapItem key={`${label}:${item}`} maxW='100%'>
            <Tag
              size='sm'
              isReadOnly
              isSelected={emphasize}
              borderColor={emphasize ? 'tags.gold.font' : 'border.light'}
              color={emphasize ? 'tags.gold.font' : undefined}
              bg={emphasize ? 'tags.gold.bg' : undefined}
              fontWeight={emphasize ? 'semibold' : 'normal'}
              maxW='100%'
              sx={CHIP_TAG_SX}
            >
              {item}
            </Tag>
          </WrapItem>
        ))}
      </Wrap>
    </Stack>
  );
};

export const OutcomeExpandedDetails: React.FC<OutcomeExpandedDetailsProps> = ({
  technicalDetails,
  stepsBreakdown,
  summary,
  fullText,
}) => {
  const phaseBlocks = useMemo(() => {
    if (!technicalDetails) return [];

    return [
      { label: 'Diagnostic', value: technicalDetails.diagnostic },
      { label: 'Action', value: technicalDetails.action },
      { label: 'Verification', value: technicalDetails.verification },
    ].filter(
      (block): block is { label: string; value: string } =>
        typeof block.value === 'string' && block.value.length > 0
    );
  }, [technicalDetails]);

  const expandedNarrative = useMemo(() => {
    const summaryText = summary?.trim() ?? '';
    const full = fullText?.trim() ?? '';
    if (!full || full === summaryText) return null;
    return full;
  }, [summary, fullText]);

  return (
    <Stack
      gap={3}
      align='stretch'
      minW={0}
      pt={3}
      mt={2}
      borderTop='1px solid'
      borderColor='border.light'
      data-cy='global-search-outcome-details'
      onClick={event => event.stopPropagation()}
    >
      {expandedNarrative ? (
        <Text
          fontSize='xs'
          lineHeight='4'
          opacity={FONTS_OPACITY}
          color='fonts.primary'
          data-cy='global-search-outcome-full-text'
        >
          {expandedNarrative}
        </Text>
      ) : null}

      {phaseBlocks.map(block => (
        <DetailBlock key={block.label} label={block.label} value={block.value} />
      ))}

      {technicalDetails?.equipment.length ? (
        <ChipGroup label='Equipment' items={technicalDetails.equipment} />
      ) : null}

      {stepsBreakdown ? (
        <Stack gap={3} align='stretch'>
          <ChipGroup label='Actions' items={stepsBreakdown.actions} />
          <ChipGroup label='Conditions' items={stepsBreakdown.conditions} />
          <ChipGroup
            label='Follow-up'
            items={stepsBreakdown.followUp}
            emphasize
          />
        </Stack>
      ) : null}
    </Stack>
  );
};
